import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { createSubscriptionEnricher, type GetToken } from '../../../shared/sgapp-subscription';
import type { AnalyticsEvent, EventPropertyValue } from '../../core/events';

/**
 * The browser-side analytics adapter, and the flag reads the page makes.
 *
 * Copied from `packages/objectives/src/analytics/client.ts`, which carries two rules this
 * file keeps unchanged:
 *
 * 1. **No key, no vendor.** With the key unset PostHog becomes a no-op. CI, local dev and
 *    the browser tests all run without live credentials — which means every one of those
 *    runs also exercises the "analytics is unreachable" configuration.
 * 2. **A vendor cannot break the flow.** Every call is wrapped. A failed capture is a lost
 *    data point; a thrown capture would be a lost upload.
 *
 * Collab adds a third, and it is the reason this file is not a straight copy:
 *
 * 3. **No URL leaves here with a fragment or a query on it.** Objectives turns session
 *    replay *on*, and its `maskCapturedNetworkRequestFn` strips query and fragment from
 *    network request *names* only — not from `$current_url`, which every captured event and
 *    every replay snapshot carries. Collab's invitation link is `…/join#<token>`, so an
 *    unscrubbed `$current_url` is the whole credential sitting in a vendor's store for seven
 *    days. `invite-token.ts` strips the fragment before this file ever initialises; the
 *    `before_send` below is the second line, for every URL from anywhere.
 *
 * The browser never sets `email` (R22). The Worker owns that property, because it is the one
 * side that has verified the address.
 */

/** The same kill switch the Worker reads. Here it only decides what the page renders. */
export const DISABLE_FLAG = 'preview-collab-disabled';

/**
 * Hides the unread markers and nothing else (R11). Read here only, never by the Worker, which
 * always returns the unread fields and keeps recording mark-reads while it is on (KTD5).
 */
export const UNREAD_DISABLE_FLAG = 'preview-collab-unread-disabled';

export interface Analytics {
  track: (event: AnalyticsEvent) => void;
  ready: boolean;
  /**
   * A positive feature gate, read permissively: an unresolved flag is not an off flag.
   *
   * Scoped to feature flags and nothing else. It must never reach invitation handling, where
   * an absent verification claim refuses (KTD14) — the two idioms point in opposite
   * directions on purpose, and mixing them is how a forwarded link admits whoever holds it.
   */
  gate: (name: string) => boolean;
  /** The kill switch, read the same permissive way: only an explicit `true` switches off. */
  disabled: () => boolean;
  /** Whether to draw unread markers. Fails open: only an explicit `true` on the switch hides. */
  unreadShown: () => boolean;
  /**
   * Calls `listener` whenever PostHog's flags (re)load, so a switch flipped after the page
   * loaded takes effect without a refresh. Returns the unsubscribe.
   */
  onFlags: (listener: () => void) => () => void;
}

const env = import.meta.env;

function swallow(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    // Deliberately not rethrown. See rule 2 above.
    console.warn(`[analytics] ${label} failed`, error);
  }
}

/** Origin and path only. Everything a fragment or a query could carry is dropped. */
export function stripUrl(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) return value;
  return value.split(/[?#]/)[0] ?? value;
}

/**
 * Every property that can hold the address the page is on, scrubbed on the way out (R9).
 *
 * `$current_url` is on ordinary events *and* on `$snapshot` replay events; `$snapshot_data`
 * additionally carries rrweb's own `href` on its meta records, which is where replay gets
 * the URL it shows above the player. All three are handled, because a token found in any one
 * of them is the same leak.
 *
 * Exported so the test asserts on this function rather than on PostHog's internals.
 */
export function scrubUrls<T extends { properties?: Record<string, unknown> } | null>(event: T): T {
  if (!event?.properties) return event;
  const properties = event.properties;

  for (const key of Object.keys(properties)) {
    if (key.endsWith('current_url') || key === '$referrer' || key === '$initial_referrer') {
      properties[key] = stripUrl(properties[key]);
    }
  }

  const snapshot = properties['$snapshot_data'];
  if (Array.isArray(snapshot)) {
    for (const record of snapshot) {
      const data: unknown = (record as { data?: unknown } | null)?.data;
      if (data && typeof data === 'object' && typeof (data as { href?: unknown }).href === 'string') {
        (data as { href: unknown }).href = stripUrl((data as { href: string }).href);
      }
    }
  }

  return event;
}

/**
 * Everything the replay must not record.
 *
 * The first three selectors are Objectives' — Clerk's account UI and any file input. The
 * rest are Collab's content: what teachers wrote to each other, what they shared, who is in
 * the group, and what the group is called. `InviteDialog` is on the list twice over, because
 * it holds both a colleague's address and the plaintext invitation token.
 */
const BLOCK_SELECTOR = [
  '.cl-rootBox',
  '.cl-userButtonPopoverCard',
  'input[type="file"]',
  '.collab-comment-box',
  '.collab-material-list',
  // The viewer renders the material itself. A replay of it is a copy of the file.
  '.collab-material-viewer',
  '.collab-member-roster',
  '.collab-group-name',
  '.collab-invite-dialog',
  // The groups-page invitations banner: every row is a group name and a colleague's address.
  '.collab-pending-invitations',
].join(', ');

function initPostHog(userId?: string): ((event: AnalyticsEvent, timestamp?: Date) => void) | null {
  const key = env.VITE_POSTHOG_KEY;
  if (!key) return null;

  posthog.init(key, {
    api_host: env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: false,
    // Explicit product events only. Replays exclude comments, materials and account UI.
    autocapture: false,
    disable_session_recording: false,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
      blockClass: 'ph-no-capture',
      blockSelector: BLOCK_SELECTOR,
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      captureCanvas: { recordCanvas: false },
      maskCapturedNetworkRequestFn: (request) => ({
        ...request, name: request.name.split(/[?#]/)[0]!,
      }),
    },
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_performance: false,
    // On for the preview-feedback survey. Its targeting (URL and once per person) lives in
    // PostHog; the survey widget renders in its own shadow root outside the replay.
    disable_surveys: false,
    // The root fix, and the one that covers replay as well as capture.
    before_send: (event: CaptureResult | null) => scrubUrls(event),
  });
  // The id and nothing else. No `setPersonProperties`, and above all no `email` (R22).
  if (userId) posthog.identify(userId);
  posthog.startSessionRecording();

  return (event, timestamp) => swallow('posthog.capture', () => posthog.capture(
    event.name,
    { ...event.properties, product: 'preview_collab', event_source: 'browser' },
    ...(timestamp ? [{ timestamp }] : []),
  ));
}

/** What runs whenever the keys are absent. */
export function nullAnalytics(): Analytics {
  return {
    track: () => {},
    ready: true,
    // Open by default. A missing PostHog must not hide the product — the Worker's
    // `decideAccess` is the boundary that actually matters, and the kill switch there
    // already fails open for exactly the same reason (R20, AE7).
    gate: () => true,
    disabled: () => false,
    unreadShown: () => true,
    onFlags: () => () => {},
  };
}

let disposeSubscription: (() => void) | undefined;

export async function createAnalytics(userId?: string, getToken?: GetToken): Promise<Analytics> {
  disposeSubscription?.();
  const subscription = createSubscriptionEnricher({ hostname: window.location.hostname });
  subscription.setIdentity(userId ?? null, getToken);
  disposeSubscription = subscription.dispose;
  let capture: ((event: AnalyticsEvent, timestamp?: Date) => void) | null = null;
  swallow('posthog.init', () => {
    capture = initPostHog(userId);
  });

  const live = capture !== null;

  // Flags are read straight off the initialised client. posthog-js resolves them
  // asynchronously after init, so an early read can answer `undefined` — which is why every
  // read below falls back to the permissive value rather than to `false`. A flag that has
  // not arrived yet must not look like a flag that is switched off.
  const flag = (name: string): boolean | string | undefined => {
    if (!live) return undefined;
    try {
      return posthog.getFeatureFlag(name) ?? undefined;
    } catch {
      return undefined;
    }
  };

  return {
    ready: true,

    /**
     * The one place `preview_collab_material_shared` reaches the vendor (KTD15).
     *
     * `growthActivity()` is consulted inside the enricher's `track` and nowhere else, so a
     * server-emitted copy of that name would never reach the growth metric — and two copies
     * of one name is the duplicate-emit failure CLAUDE.md records against Objectives.
     */
    track: (event) => {
      if (capture) {
        subscription.track(event, (enriched, timestamp) => capture?.({
          name: event.name,
          // The enricher's property type admits `string[]`; the event vocabulary does not,
          // and the properties it actually adds are the subscription scalars. The cast says
          // so rather than widening `EventPropertyValue` to admit an array no builder emits.
          properties: enriched.properties as Record<string, EventPropertyValue>,
        }, timestamp));
      }
    },

    gate: (name) => flag(name) !== false,

    // Only an explicit `true` takes the tool away. Unset, unresolved and unreadable all
    // leave it up — the switch fails open, and CLAUDE.md says to say which and mean it.
    disabled: () => flag(DISABLE_FLAG) === true,

    // The same rule for the markers' switch.
    unreadShown: () => flag(UNREAD_DISABLE_FLAG) !== true,

    onFlags: (listener) => {
      if (!live) return () => {};
      try {
        const stop = posthog.onFeatureFlags(() => swallow('flags listener', listener));
        return () => swallow('flags unsubscribe', () => stop());
      } catch {
        return () => {};
      }
    },
  };
}

/** Correlation only; the Worker still derives the user from the verified Clerk token. */
export function posthogHeaders(): Record<string, string> {
  if (!env.VITE_POSTHOG_KEY) return {};
  try {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();
    return distinctId && sessionId
      ? { 'X-POSTHOG-DISTINCT-ID': distinctId, 'X-POSTHOG-SESSION-ID': sessionId }
      : {};
  } catch { return {}; }
}

/** Stop recording authentication screens and prevent identities crossing account changes. */
export function stopAnalytics(): void {
  disposeSubscription?.();
  disposeSubscription = undefined;
  if (!env.VITE_POSTHOG_KEY) return;
  swallow('stop', () => { posthog.stopSessionRecording(); posthog.reset(); });
}
